import { Routes } from "@angular/router";
import { environment } from "../environments/environment";
import { HomepageNewComponent } from "./pages/home/home.component";
import { AuthGuard } from "./guards/auth.guard";

const baseRoutes: Routes = [
  // Keep home component eagerly loaded for initial page load performance
  { path: "", component: HomepageNewComponent },

  // Lazy load all other routes to reduce initial bundle size
  {
    path: "for-designers",
    loadComponent: () =>
      import("./pages/for-designers/for-designers.component").then(
        (m) => m.ForDesignersComponent
      ),
  },
  {
    path: "salary-guide",
    loadComponent: () =>
      import("./pages/salary-guide/salary-guide.component").then(
        (m) => m.SalaryGuideComponent
      ),
  },
  {
    path: "blog",
    loadComponent: () =>
      import("./pages/blog/blog-index.component").then(
        (m) => m.BlogIndexComponent
      ),
  },
  {
    path: "overview",
    redirectTo: "hire-designers",
    pathMatch: "full",
  },
  {
    path: "hire-designers",
    loadComponent: () =>
      import("./pages/hire-designers/hire-designers.component").then(
        (m) => m.HireDesignersComponent
      ),
  },
  {
    path: "post-job",
    loadComponent: () =>
      import("./pages/post-job/post-job.component").then(
        (m) => m.PostJobComponent
      ),
  },
  {
    path: "about",
    loadComponent: () =>
      import("./pages/about/about.component").then((m) => m.AboutComponent),
  },
  {
    path: "contact",
    loadComponent: () =>
      import("./pages/contact/contact.component").then(
        (m) => m.ContactComponent
      ),
  },
  {
    path: "privacy",
    loadComponent: () =>
      import("./pages/privacy/privacy.component").then(
        (m) => m.PrivacyComponent
      ),
  },
  {
    path: "terms",
    loadComponent: () =>
      import("./pages/terms/terms.component").then((m) => m.TermsComponent),
  },
  {
    path: "cookies",
    loadComponent: () =>
      import("./pages/cookies/cookies.component").then(
        (m) => m.CookiesComponent
      ),
  },
  {
    path: "browse-jobs",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  {
    path: "browse-jobs/:category",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  // NEW: Pretty URL routes for SEO
  {
    path: "jobs",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  // Location directory hub — must precede "jobs/:category" so the literal
  // path is not swallowed by the category param.
  {
    path: "jobs/locations",
    loadComponent: () =>
      import("./pages/location-directory/location-directory.component").then(
        (m) => m.LocationDirectoryComponent
      ),
  },
  {
    path: "jobs/:category",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  {
    path: "jobs/:category/remote",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  {
    path: "jobs/remote/:location",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  {
    path: "jobs/:category/:location",
    loadComponent: () =>
      import("./pages/browse-jobs/browse-jobs.component").then(
        (m) => m.BrowseJobsComponent
      ),
  },
  {
    path: "job/:id",
    loadComponent: () =>
      import("./pages/job-details/job-details.component").then(
        (m) => m.JobDetailsComponent
      ),
  },
  // Company directory hub. Without this, /company fell through to the
  // ":username" catch-all and rendered "Profile not found" — a dead end for
  // the only internal link into 2,000+ company pages.
  {
    path: "company",
    loadComponent: () =>
      import("./pages/company-directory/company-directory.component").then(
        (m) => m.CompanyDirectoryComponent
      ),
  },
  {
    path: "company/:companySlug",
    loadComponent: () =>
      import("./pages/companies/companies.component").then(
        (m) => m.CompaniesComponent
      ),
  },
  {
    path: "ai-companies",
    loadComponent: () =>
      import("./pages/ai-companies/ai-companies.component").then(
        (m) => m.AICompaniesComponent
      ),
  },
  {
    path: "blog/:id",
    loadComponent: () =>
      import("./pages/blog/blog.component").then((m) => m.BlogComponent),
  },
  {
    path: "ai-job-matcher",
    loadComponent: () =>
      import("./pages/ai-job-matcher/ai-job-matcher.component").then(
        (m) => m.AiJobMatcherComponent
      ),
  },
  {
    path: "dashboard",
    loadComponent: () =>
      import("./pages/dashboard/dashboard.component").then(
        (m) => m.DashboardComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "job-matches",
    loadComponent: () =>
      import("./pages/job-matches/job-matches.component").then(
        (m) => m.JobMatchesPageComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "job-alerts",
    loadComponent: () =>
      import("./pages/job-alerts/job-alerts.component").then(
        (m) => m.JobAlertsComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "companies-hiring-this-week",
    loadComponent: () =>
      import(
        "./pages/companies-hiring-this-week/companies-hiring-this-week.component"
      ).then((m) => m.CompaniesHiringThisWeekComponent),
  },
  {
    path: "payment-success",
    loadComponent: () =>
      import("./pages/payment-success/payment-success.component").then(
        (m) => m.PaymentSuccessComponent
      ),
  },
  {
    path: "portfolios",
    loadComponent: () =>
      import("./pages/portfolios/portfolios.component").then(
        (m) => m.PortfoliosComponent
      ),
  },
  {
    path: "portfolios/:category",
    loadComponent: () =>
      import("./pages/portfolio-category/portfolio-category.component").then(
        (m) => m.PortfolioCategoryComponent
      ),
  },

  // Talent profile (logged-in user's profile and public view by id)
  {
    path: "profile",
    loadComponent: () =>
      import("./pages/talent-profile/talent-profile.component").then(
        (m) => m.TalentProfileComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "talent/:id",
    loadComponent: () =>
      import("./pages/talent-profile/talent-profile.component").then(
        (m) => m.TalentProfileComponent
      ),
  },

  // RSS routes - will be handled by server in production, but need fallback for development
  { path: "rss", redirectTo: "/", pathMatch: "full" },
  { path: "rss.xml", redirectTo: "/", pathMatch: "full" },
  { path: "feed.xml", redirectTo: "/", pathMatch: "full" },

  // Public profile by username (must be last - catch-all)
  {
    path: ":username",
    loadComponent: () =>
      import("./pages/public-profile/public-profile.component").then(
        (m) => m.PublicProfileComponent
      ),
  },
];

// Development-only routes (talent pool features not ready for production)
const developmentRoutes: Routes = [
  {
    path: "find-talent",
    loadComponent: () =>
      import("./pages/find-talent/find-talent.component").then(
        (m) => m.FindTalentComponent
      ),
  },
  {
    path: "find-talent-v2",
    loadComponent: () =>
      import("./pages/find-talent-v2/find-talent-v2.component").then(
        (m) => m.FindTalentV2Component
      ),
  },
];

// Conditionally add development routes
export const routes: Routes = [
  ...baseRoutes,
  ...(environment.production ? [] : developmentRoutes),
];
